<template>
  <el-dialog
    v-model="visible"
    :show-close="false"
    width="480"
    class="hide-header"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
  >
    <div class="dialog-content mfa-dialog">
      <h3>Verification Required</h3>
      <p>We've sent a 6-digit verification code to your email. Please enter it below.</p>

      <div class="mfa-input-wrap">
        <el-input
          v-model="code"
          placeholder="Enter 6-digit code"
          maxlength="6"
          :disabled="submitting"
          @keyup.enter="handleSubmit"
        />
        <el-button
          type="text"
          :disabled="resendCountdown > 0 || sendingCode"
          :loading="sendingCode"
          @click="resendCode"
        >
          {{ resendCountdown > 0 ? `Resend code (${resendCountdown}s)` : 'Resend code' }}
        </el-button>
      </div>
      <p v-if="errorMsg" class="mfa-error">{{ errorMsg }}</p>
      <footer>
        <el-button plain size="large" @click="handleCancel" :disabled="submitting">Cancel</el-button>
        <el-button
          type="primary"
          size="large"
          :loading="submitting"
          :disabled="code.length !== 6"
          @click="handleSubmit"
        >
          Verify
        </el-button>
      </footer>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref } from 'vue';
import ajax from '@/utils/Ajax';

const visible = ref(false);
const code = ref('');
const errorMsg = ref('');
const submitting = ref(false);
const sendingCode = ref(false);
const resendCountdown = ref(0);

let resolveFn = null;
let rejectFn = null;
let countdownTimer = null;
let currentMfaType = null;

const startCountdown = (seconds = 60) => {
  resendCountdown.value = seconds;
  clearInterval(countdownTimer);
  countdownTimer = setInterval(() => {
    resendCountdown.value--;
    if (resendCountdown.value <= 0) {
      clearInterval(countdownTimer);
    }
  }, 1000);
};

const sendCode = async () => {
  sendingCode.value = true;
  try {
    await ajax.get('/tipalti/send-verify-code', { params: { mfa_type: currentMfaType } });
    startCountdown(60);
  } catch (err) {
    errorMsg.value = 'Failed to send verification code. Please try again.';
  } finally {
    sendingCode.value = false;
  }
};

const resendCode = () => {
  errorMsg.value = '';
  sendCode();
};

const verify = (mfa_type = null) => {
  currentMfaType = mfa_type;
  code.value = '';
  errorMsg.value = '';
  submitting.value = false;
  visible.value = true;

  sendCode();

  return new Promise((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });
};

const handleSubmit = () => {
  if (code.value.length !== 6) return;
  submitting.value = true;
  errorMsg.value = '';
  resolveFn(code.value);
};

const handleCancel = () => {
  visible.value = false;
  clearInterval(countdownTimer);
  rejectFn('cancelled');
};

const showError = (msg = 'Invalid verification code. Please try again.') => {
  errorMsg.value = msg;
  submitting.value = false;
  return new Promise((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });
};

const closeDialog = () => {
  visible.value = false;
  clearInterval(countdownTimer);
};

defineExpose({ verify, showError, closeDialog });
</script>

<style lang="scss">
.mfa-dialog {
  padding: 8px;

  h3 {
    font: var(--medium-24);
    color: var(--Text-primary);
  }

  p {
    font: var(--book-14);
    color: var(--Text-secondary);
    margin: 8px 0 24px;
  }

  .mfa-input-wrap {
    margin: 0 0 8px;
    display: flex;
    gap: 16px;
    align-items: center;
  }

  .mfa-error {
    color: var(--Red-30);
    font: var(--book-12);
    margin: 4px 0 0;
    text-align: left;
  }

  footer {
    display: flex;
    gap: 16px;
    padding: 8px 0 0;

    .ui-button {
      flex: 1;
      margin: 0 !important;
    }
  }
}
</style>
